/**
 * Cross-app channel for sending a pre-composed prompt to the shell-hosted
 * Robin Assistant.
 *
 * A dashboard app that knows what the user is looking at — the tickets
 * issue-report sider, say — can compose a prompt about it and hand it to the
 * assistant, instead of the user retyping the context into the chat. The shell
 * owns the assistant: it opens the sider and forwards the request into the
 * agent parcel (see `apps/shell/src/lib/agentPromptBridge.ts`). MFEs never
 * touch the parcel directly.
 *
 * This is an EVENT channel, not a state channel — contrast
 * `composerUiChannel`, where the latest message is truth and a late subscriber
 * can safely miss earlier ones. A prompt is an action: it is delivered once
 * and never replayed, because replaying it to a late subscriber would send the
 * prompt a second time. `requestId` makes that guarantee checkable at the
 * receiving end rather than assumed.
 *
 * ******* DO NOT MAKE BACKWARDS INCOMPATIBLE CHANGES *******
 * Shell and apps deploy independently, and the agent parcel is a third,
 * separately deployed bundle from another repo. Renaming the channel constant
 * or removing a payload field can silently break senders or receivers that
 * have not yet been redeployed.
 *
 * Safe changes:
 *   - Add a new optional field to `AgentPromptRequest`.
 *
 * Unsafe changes:
 *   - Rename or remove the channel constant.
 *   - Remove or rename a field on `AgentPromptRequest`.
 *   - Change a field's type or semantics.
 *
 * If an incompatible change is unavoidable, introduce a new constant
 * (`dashboard:agent:prompt:v2`) and run both for at least one release.
 *
 * `AgentPromptRequest` is mirrored structurally in robin-ai-agents
 * (web/src/utils/shellBridge.ts). The two repos duplicate rather than import,
 * so a change here needs the same change there.
 */
/** The bridge channel name used as the transport. */
export var AGENT_PROMPT_CHANNEL = 'dashboard:agent:prompt';
